import React, { useState, useEffect } from "react";
import { getActiveAds } from "../../../api/api";
import { CircularProgress, Alert, Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
// Demo images for landscape ads
import DemoLandscape1 from "../../../assests/landscape/1.png";
import DemoLandscape2 from "../../../assests/landscape/2.png";
import DemoLandscape3 from "../../../assests/landscape/3.png";
import DemoLandscape4 from "../../../assests/landscape/4.png";

const Landscape = () => {
    const [ads, setAds] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();

    // Get parameters from URL
    const branchId = searchParams.get("branch_id");
    const token = searchParams.get("token");
    const clientId = searchParams.get("client_id");
    const interval = parseInt(searchParams.get("interval")) || 5000; // Default 5 seconds

    // Demo ads for demo mode
    const demoAds = [
        {
            id: "demo-landscape-1",
            title: "Demo Landscape Ad 1",
            image_url: DemoLandscape1,
            description: "Sample landscape advertisement",
        },
        {
            id: "demo-landscape-2",
            title: "Demo Landscape Ad 2",
            image_url: DemoLandscape2,
            description: "Sample landscape advertisement",
        },
        {
            id: "demo-landscape-3",
            title: "Demo Landscape Ad 3",
            image_url: DemoLandscape3,
            description: "Sample landscape advertisement",
        },
        {
            id: "demo-landscape-4",
            title: "Demo Landscape Ad 4",
            image_url: DemoLandscape4,
            description: "Sample landscape advertisement",
        },
    ];

    // Check if we're in demo mode (internal access without external parameters)
    const isDemoMode = !branchId && !token && !clientId;

    // Add debug logging
    console.log("Landscape component - Demo mode check:", {
        branchId,
        token,
        clientId,
        isDemoMode,
        url: window.location.href,
    });

    useEffect(() => {
        fetchActiveAds();
    }, [branchId, token, clientId, isDemoMode]);

    // Auto-refresh ads every 60 seconds
    useEffect(() => {
        const refreshInterval = setInterval(() => {
            console.log("Auto-refreshing landscape ads...");
            fetchActiveAds();
        }, 60000); // 60 seconds

        return () => clearInterval(refreshInterval);
    }, [branchId, token, clientId, isDemoMode]);

    const fetchActiveAds = async () => {
        try {
            setLoading(true);
            console.log("Landscape fetchActiveAds - Starting fetch:", {
                branchId,
                token,
                clientId,
                isDemoMode,
            });

            // If external access with parameters, use them
            if (branchId && token) {
                console.log("External access detected, fetching real ads");
                const response = await getActiveAds(
                    "landscape",
                    clientId,
                    branchId
                );
                console.log("External API response:", response);
                if (response.success) {
                    setAds(response.data);
                } else {
                    setError("Failed to load ads");
                }
            } else {
                // Internal access - check demo mode first
                if (isDemoMode) {
                    console.log(
                        "Demo mode detected, using demo ads immediately"
                    );
                    setAds(demoAds);
                    setError(null);
                    return;
                }

                // Normal internal access (not demo mode)
                console.log("Internal access detected, fetching real ads");
                const response = await getActiveAds(
                    "landscape",
                    clientId,
                    branchId
                );
                console.log("Internal API response:", response);

                if (response.success && response.data.length > 0) {
                    console.log("Using real ads:", response.data);
                    setAds(response.data);
                } else {
                    console.log("No real ads available, setting empty array");
                    setAds([]);
                }
            }
            setError(null);
        } catch (err) {
            console.error("Error fetching ads:", err);
            // In demo mode, use demo ads even if there's an error
            if (isDemoMode) {
                console.log("Demo mode: Using demo landscape ads due to error");
                setAds(demoAds);
            } else {
                setError("Error loading ads");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (ads.length > 0) {
            const intervalTimer = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
            }, interval); // Use interval from URL parameter

            return () => clearInterval(intervalTimer);
        }
    }, [ads, interval]);

    // Handle window resize to ensure images remain responsive
    useEffect(() => {
        const handleResize = () => {
            // Force re-render on resize to recalculate image dimensions
            const img = document.querySelector('img[alt*="landscape"]');
            if (img) {
                // Trigger image recalculation
                const event = new Event('resize');
                window.dispatchEvent(event);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [currentIndex]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100%",
                    "@media (max-width: 768px)": {
                        height: "100vh",
                    },
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100%",
                    padding: 2,
                    "@media (max-width: 768px)": {
                        height: "100vh",
                        padding: 1,
                    },
                }}
            >
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (ads.length === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100%",
                    padding: 2,
                    "@media (max-width: 768px)": {
                        height: "100vh",
                        padding: 1,
                    },
                }}
            >
                <Alert severity="info">No active landscape ads available</Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100vh",
                padding: { xs: 0.5, sm: 1, md: 2 },
                boxSizing: "border-box",
                "@media (max-width: 768px)": {
                    height: "100vh",
                    padding: 1,
                },
                "@media (max-width: 480px)": {
                    padding: 0.5,
                },
            }}
        >
            <img
                src={ads[currentIndex].image_url}
                alt={ads[currentIndex].title}
                style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    objectPosition: "center center",
                    transition: "opacity 1s ease-in-out",
                    display: "block",
                    margin: "0 auto",
                }}
                onLoad={(e) => {
                    // Ensure image scales properly on load
                    const img = e.target;
                    const container = img.parentElement;
                    if (container) {
                        const containerWidth = container.clientWidth;
                        const containerHeight = container.clientHeight;
                        const imgAspectRatio = img.naturalWidth / img.naturalHeight;
                        const containerAspectRatio = containerWidth / containerHeight;
                        
                        // Adjust sizing based on aspect ratios
                        if (imgAspectRatio > containerAspectRatio) {
                            img.style.width = "100%";
                            img.style.height = "auto";
                        } else {
                            img.style.width = "auto";
                            img.style.height = "100%";
                        }
                    }
                }}
            />
        </Box>
    );
};

export default Landscape;
